import { HttpClient, HttpHeaders, HttpResponse, HttpResponseBase } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { Observable, of as _observableOf, throwError as _observableThrow } from 'rxjs';
import { catchError as _observableCatch, mergeMap as _observableMergeMap } from 'rxjs/operators';
import { CommonFunctions } from '../common-functions';
import { DeviceModelAddInput } from '../models/device-model-add-input.model';
import { DeviceModelAddOutput } from '../models/device-model-add-output.model';
import { DeviceModelPortSpecificationSelectByIdOutput } from '../models/device-model-port-specification-select-by-id-output.model';
import { DeviceModelSelectActiveOutput } from '../models/device-model-select-active-output.model';
import { DeviceModelSelectAllOutput } from '../models/device-model-select-all-output.model';
import { DeviceModelSelectByIdOutput } from '../models/device-model-select-by-id-output.model';
import { DeviceModelSpecificationPortAddInput } from '../models/device-model-specification-port-add-input.model';
import { DeviceModelSpecificationPortAddOutput } from '../models/device-model-specification-port-add-output.model';
import { DeviceModelSpecificationSelectActiveOutput } from '../models/device-model-specification-select-active-output.model';
import { DeviceModelSpecificationSelectAllOutput } from '../models/device-model-specification-select-all-output.model';
import { DeviceModelSpecificationStatusInput } from '../models/device-model-specification-status-input.model';
import { DeviceModelSpecificationStatusOutput } from '../models/device-model-specification-status-output.model';
import { DeviceModelSpecificationUpdateInput } from '../models/device-model-specification-update-input.model';
import { DeviceModelSpecificationUpdateOutput } from '../models/device-model-specification-update-output.model';
import { DeviceModelStatusInput } from '../models/device-model-status-input.model';
import { DeviceModelStatusOutput } from '../models/device-model-status-output.model';
import { DeviceModelUpdateInput } from '../models/device-model-update-input.model';
import { DeviceModelUpdateOutput } from '../models/device-model-update-output.model';
import { API_BASE_URL } from '../service-proxies';

@Injectable()
export class DeviceModelServiceProxy {
    private http: HttpClient;
    private baseUrl: string;
    protected jsonParseReviver: ((key: string, value: any) => any) | undefined = undefined;

    constructor(private _commonFunctions: CommonFunctions, @Inject(HttpClient) http: HttpClient, @Optional() @Inject(API_BASE_URL) baseUrl?: string) {
        this.http = http;
        this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : "http://localhost:3001/api/v1";
    }

    /**
     * @return Device model added successfully.
     */
    add(body: DeviceModelAddInput): Observable<DeviceModelAddOutput> {
        let url_ = this.baseUrl + "/DeviceModel/Add";
        url_ = url_.replace(/[?&]$/, "");

        const content_ = JSON.stringify(body);

        let options_ : any = {
            body: content_,
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json",
                "Accept": "application/json"
            })
        };

        return this.http.request("post", url_, options_).pipe(_observableMergeMap((response_ : any) => {
            return this.processAdd(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processAdd(<any>response_);
                } catch (e) {
                    return <Observable<DeviceModelAddOutput>><any>_observableThrow(e);
                }
            } else
                return <Observable<DeviceModelAddOutput>><any>_observableThrow(response_);
        }));
    }

    protected processAdd(response: HttpResponseBase): Observable<DeviceModelAddOutput> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
            (<any>response).error instanceof Blob ? (<any>response).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); }}
        if (status === 200) {
            return this._commonFunctions.blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            let result200: any = null;
            let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result200 = DeviceModelAddOutput.fromJS(resultData200);
            return _observableOf(result200);
            }));
        } else if (status !== 200 && status !== 204) {
            return this._commonFunctions.blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            return this._commonFunctions.throwException("An unexpected server error occurred.", status, _responseText, _headers);
            }));
        }
        return _observableOf<DeviceModelAddOutput>(<any>null);
    }

    /**
     * @return Device model updated successfully.
     */
    update(body: DeviceModelUpdateInput): Observable<DeviceModelUpdateOutput> {
        let url_ = this.baseUrl + "/DeviceModel/Update";
        url_ = url_.replace(/[?&]$/, "");

        const content_ = JSON.stringify(body);

        let options_ : any = {
            body: content_,
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json",
                "Accept": "application/json"
            })
        };

        return this.http.request("put", url_, options_).pipe(_observableMergeMap((response_ : any) => {
            return this.processUpdate(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processUpdate(<any>response_);
                } catch (e) {
                    return <Observable<DeviceModelUpdateOutput>><any>_observableThrow(e);
                }
            } else
                return <Observable<DeviceModelUpdateOutput>><any>_observableThrow(response_);
        }));
    }

    protected processUpdate(response: HttpResponseBase): Observable<DeviceModelUpdateOutput> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
            (<any>response).error instanceof Blob ? (<any>response).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); }}
        if (status === 200) {
            return this._commonFunctions.blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            let result200: any = null;
            let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result200 = DeviceModelUpdateOutput.fromJS(resultData200);
            return _observableOf(result200);
            }));
        } else if (status !== 200 && status !== 204) {
            return this._commonFunctions.blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            return this._commonFunctions.throwException("An unexpected server error occurred.", status, _responseText, _headers);
            }));
        }
        return _observableOf<DeviceModelUpdateOutput>(<any>null);
    }

    /**
     * @return Device model status updated successfully.
     */
    status(body: DeviceModelStatusInput): Observable<DeviceModelStatusOutput> {
        let url_ = this.baseUrl + "/DeviceModel/Status";
        url_ = url_.replace(/[?&]$/, "");

        const content_ = JSON.stringify(body);

        let options_ : any = {
            body: content_,
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json",
                "Accept": "application/json"
            })
        };

        return this.http.request("put", url_, options_).pipe(_observableMergeMap((response_ : any) => {
            return this.processStatus(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processStatus(<any>response_);
                } catch (e) {
                    return <Observable<DeviceModelStatusOutput>><any>_observableThrow(e);
                }
            } else
                return <Observable<DeviceModelStatusOutput>><any>_observableThrow(response_);
        }));
    }

    protected processStatus(response: HttpResponseBase): Observable<DeviceModelStatusOutput> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
            (<any>response).error instanceof Blob ? (<any>response).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); }}
        if (status === 200) {
            return this._commonFunctions.blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            let result200: any = null;
            let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result200 = DeviceModelStatusOutput.fromJS(resultData200);
            return _observableOf(result200);
            }));
        } else if (status !== 200 && status !== 204) {
            return this._commonFunctions.blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            return this._commonFunctions.throwException("An unexpected server error occurred.", status, _responseText, _headers);
            }));
        }
        return _observableOf<DeviceModelStatusOutput>(<any>null);
    }

    /**
     * @return All device model selected successfully.
     */
    selectAll(deviceBrandId: number): Observable<DeviceModelSelectAllOutput> {
        let url_ = this.baseUrl + "/DeviceModel/SelectAll/{deviceBrandId}";
        if (deviceBrandId === undefined || deviceBrandId === null)
            throw new Error("The parameter 'deviceBrandId' must be defined.");
        url_ = url_.replace("{deviceBrandId}", encodeURIComponent("" + deviceBrandId));
        url_ = url_.replace(/[?&]$/, "");

        let options_ : any = {
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Accept": "application/json"
            })
        };

        return this.http.request("get", url_, options_).pipe(_observableMergeMap((response_ : any) => {
            return this.processSelectAll(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processSelectAll(<any>response_);
                } catch (e) {
                    return <Observable<DeviceModelSelectAllOutput>><any>_observableThrow(e);
                }
            } else
                return <Observable<DeviceModelSelectAllOutput>><any>_observableThrow(response_);
        }));
    }

    protected processSelectAll(response: HttpResponseBase): Observable<DeviceModelSelectAllOutput> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
            (<any>response).error instanceof Blob ? (<any>response).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); }}
        if (status === 200) {
            return this._commonFunctions.blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            let result200: any = null;
            let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result200 = DeviceModelSelectAllOutput.fromJS(resultData200);
            return _observableOf(result200);
            }));
        } else if (status !== 200 && status !== 204) {
            return this._commonFunctions.blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            return this._commonFunctions.throwException("An unexpected server error occurred.", status, _responseText, _headers);
            }));
        }
        return _observableOf<DeviceModelSelectAllOutput>(<any>null);
    }

    /**
     * @return Active device model selected successfully.
     */
    selectActive(deviceBrandId: number): Observable<DeviceModelSelectActiveOutput> {
        let url_ = this.baseUrl + "/DeviceModel/SelectActive/{deviceBrandId}";
        if (deviceBrandId === undefined || deviceBrandId === null)
            throw new Error("The parameter 'deviceBrandId' must be defined.");
        url_ = url_.replace("{deviceBrandId}", encodeURIComponent("" + deviceBrandId));
        url_ = url_.replace(/[?&]$/, "");

        let options_ : any = {
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Accept": "application/json"
            })
        };

        return this.http.request("get", url_, options_).pipe(_observableMergeMap((response_ : any) => {
            return this.processSelectActive(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processSelectActive(<any>response_);
                } catch (e) {
                    return <Observable<DeviceModelSelectActiveOutput>><any>_observableThrow(e);
                }
            } else
                return <Observable<DeviceModelSelectActiveOutput>><any>_observableThrow(response_);
        }));
    }

    protected processSelectActive(response: HttpResponseBase): Observable<DeviceModelSelectActiveOutput> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
            (<any>response).error instanceof Blob ? (<any>response).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); }}
        if (status === 200) {
            return this._commonFunctions.blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            let result200: any = null;
            let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result200 = DeviceModelSelectActiveOutput.fromJS(resultData200);
            return _observableOf(result200);
            }));
        } else if (status !== 200 && status !== 204) {
            return this._commonFunctions.blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            return this._commonFunctions.throwException("An unexpected server error occurred.", status, _responseText, _headers);
            }));
        }
        return _observableOf<DeviceModelSelectActiveOutput>(<any>null);
    }

    /**
     * @return Device model selected successfully.
     */
    selectById(deviceModelId: number): Observable<DeviceModelSelectByIdOutput> {
        let url_ = this.baseUrl + "/DeviceModel/SelectById/{deviceModelId}";
        if (deviceModelId === undefined || deviceModelId === null)
            throw new Error("The parameter 'deviceModelId' must be defined.");
        url_ = url_.replace("{deviceModelId}", encodeURIComponent("" + deviceModelId));
        url_ = url_.replace(/[?&]$/, "");

        let options_ : any = {
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Accept": "application/json"
            })
        };

        return this.http.request("get", url_, options_).pipe(_observableMergeMap((response_ : any) => {
            return this.processSelectById(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processSelectById(<any>response_);
                } catch (e) {
                    return <Observable<DeviceModelSelectByIdOutput>><any>_observableThrow(e);
                }
            } else
                return <Observable<DeviceModelSelectByIdOutput>><any>_observableThrow(response_);
        }));
    }

    protected processSelectById(response: HttpResponseBase): Observable<DeviceModelSelectByIdOutput> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
            (<any>response).error instanceof Blob ? (<any>response).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); }}
        if (status === 200) {
            return this._commonFunctions.blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            let result200: any = null;
            let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result200 = DeviceModelSelectByIdOutput.fromJS(resultData200);
            return _observableOf(result200);
            }));
        } else if (status !== 200 && status !== 204) {
            return this._commonFunctions.blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            return this._commonFunctions.throwException("An unexpected server error occurred.", status, _responseText, _headers);
            }));
        }
        return _observableOf<DeviceModelSelectByIdOutput>(<any>null);
    }

    /**
     * @return Device model specification added successfully.
     */
    addDeviceModelPortSpecification(body: DeviceModelSpecificationPortAddInput): Observable<DeviceModelSpecificationPortAddOutput> {
        let url_ = this.baseUrl + "/DeviceModel/AddDeviceModelPortSpecification";
        url_ = url_.replace(/[?&]$/, "");

        const content_ = JSON.stringify(body);

        let options_ : any = {
            body: content_,
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json",
                "Accept": "application/json"
            })
        };

        return this.http.request("post", url_, options_).pipe(_observableMergeMap((response_ : any) => {
            return this.processAddDeviceModelPortSpecification(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processAddDeviceModelPortSpecification(<any>response_);
                } catch (e) {
                    return <Observable<DeviceModelSpecificationPortAddOutput>><any>_observableThrow(e);
                }
            } else
                return <Observable<DeviceModelSpecificationPortAddOutput>><any>_observableThrow(response_);
        }));
    }

    protected processAddDeviceModelPortSpecification(response: HttpResponseBase): Observable<DeviceModelSpecificationPortAddOutput> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
            (<any>response).error instanceof Blob ? (<any>response).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); }}
        if (status === 200) {
            return this._commonFunctions.blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            let result200: any = null;
            let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result200 = DeviceModelSpecificationPortAddOutput.fromJS(resultData200);
            return _observableOf(result200);
            }));
        } else if (status !== 200 && status !== 204) {
            return this._commonFunctions.blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            return this._commonFunctions.throwException("An unexpected server error occurred.", status, _responseText, _headers);
            }));
        }
        return _observableOf<DeviceModelSpecificationPortAddOutput>(<any>null);
    }

    /**
     * @return Device model specification updated successfully.
     */
    updateDeviceModelPortSpecification(body: DeviceModelSpecificationUpdateInput): Observable<DeviceModelSpecificationUpdateOutput> {
        let url_ = this.baseUrl + "/DeviceModel/UpdateDeviceModelPortSpecification";
        url_ = url_.replace(/[?&]$/, "");

        const content_ = JSON.stringify(body);

        let options_ : any = {
            body: content_,
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json",
                "Accept": "application/json"
            })
        };

        return this.http.request("put", url_, options_).pipe(_observableMergeMap((response_ : any) => {
            return this.processUpdateDeviceModelPortSpecification(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processUpdateDeviceModelPortSpecification(<any>response_);
                } catch (e) {
                    return <Observable<DeviceModelSpecificationUpdateOutput>><any>_observableThrow(e);
                }
            } else
                return <Observable<DeviceModelSpecificationUpdateOutput>><any>_observableThrow(response_);
        }));
    }

    protected processUpdateDeviceModelPortSpecification(response: HttpResponseBase): Observable<DeviceModelSpecificationUpdateOutput> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
            (<any>response).error instanceof Blob ? (<any>response).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); }}
        if (status === 200) {
            return this._commonFunctions.blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            let result200: any = null;
            let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result200 = DeviceModelSpecificationUpdateOutput.fromJS(resultData200);
            return _observableOf(result200);
            }));
        } else if (status !== 200 && status !== 204) {
            return this._commonFunctions.blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            return this._commonFunctions.throwException("An unexpected server error occurred.", status, _responseText, _headers);
            }));
        }
        return _observableOf<DeviceModelSpecificationUpdateOutput>(<any>null);
    }

    /**
     * @return Device model specification status updated successfully.
     */
    statusDeviceModelPortSpecification(body: DeviceModelSpecificationStatusInput): Observable<DeviceModelSpecificationStatusOutput> {
        let url_ = this.baseUrl + "/DeviceModel/StatusDeviceModelPortSpecification";
        url_ = url_.replace(/[?&]$/, "");

        const content_ = JSON.stringify(body);

        let options_ : any = {
            body: content_,
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json",
                "Accept": "application/json"
            })
        };

        return this.http.request("put", url_, options_).pipe(_observableMergeMap((response_ : any) => {
            return this.processStatusDeviceModelPortSpecification(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processStatusDeviceModelPortSpecification(<any>response_);
                } catch (e) {
                    return <Observable<DeviceModelSpecificationStatusOutput>><any>_observableThrow(e);
                }
            } else
                return <Observable<DeviceModelSpecificationStatusOutput>><any>_observableThrow(response_);
        }));
    }

    protected processStatusDeviceModelPortSpecification(response: HttpResponseBase): Observable<DeviceModelSpecificationStatusOutput> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
            (<any>response).error instanceof Blob ? (<any>response).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); }}
        if (status === 200) {
            return this._commonFunctions.blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            let result200: any = null;
            let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result200 = DeviceModelSpecificationStatusOutput.fromJS(resultData200);
            return _observableOf(result200);
            }));
        } else if (status !== 200 && status !== 204) {
            return this._commonFunctions.blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            return this._commonFunctions.throwException("An unexpected server error occurred.", status, _responseText, _headers);
            }));
        }
        return _observableOf<DeviceModelSpecificationStatusOutput>(<any>null);
    }

    /**
     * @return All device model specification selected successfully.
     */
    selectAllDeviceModelPortSpecification(deviceModelId: number): Observable<DeviceModelSpecificationSelectAllOutput> {
        let url_ = this.baseUrl + "/DeviceModel/SelectAllDeviceModelPortSpecification/{deviceModelId}";
        if (deviceModelId === undefined || deviceModelId === null)
            throw new Error("The parameter 'deviceModelId' must be defined.");
        url_ = url_.replace("{deviceModelId}", encodeURIComponent("" + deviceModelId));
        url_ = url_.replace(/[?&]$/, "");

        let options_ : any = {
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Accept": "application/json"
            })
        };

        return this.http.request("get", url_, options_).pipe(_observableMergeMap((response_ : any) => {
            return this.processSelectAllDeviceModelPortSpecification(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processSelectAllDeviceModelPortSpecification(<any>response_);
                } catch (e) {
                    return <Observable<DeviceModelSpecificationSelectAllOutput>><any>_observableThrow(e);
                }
            } else
                return <Observable<DeviceModelSpecificationSelectAllOutput>><any>_observableThrow(response_);
        }));
    }

    protected processSelectAllDeviceModelPortSpecification(response: HttpResponseBase): Observable<DeviceModelSpecificationSelectAllOutput> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
            (<any>response).error instanceof Blob ? (<any>response).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); }}
        if (status === 200) {
            return this._commonFunctions.blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            let result200: any = null;
            let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result200 = DeviceModelSpecificationSelectAllOutput.fromJS(resultData200);
            return _observableOf(result200);
            }));
        } else if (status !== 200 && status !== 204) {
            return this._commonFunctions.blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            return this._commonFunctions.throwException("An unexpected server error occurred.", status, _responseText, _headers);
            }));
        }
        return _observableOf<DeviceModelSpecificationSelectAllOutput>(<any>null);
    }

    /**
     * @return Active device model specification selected successfully.
     */
    selectActiveDeviceModelPortSpecification(deviceModelId: number): Observable<DeviceModelSpecificationSelectActiveOutput> {
        let url_ = this.baseUrl + "/DeviceModel/SelectActiveDeviceModelPortSpecification/{deviceModelId}";
        if (deviceModelId === undefined || deviceModelId === null)
            throw new Error("The parameter 'deviceModelId' must be defined.");
        url_ = url_.replace("{deviceModelId}", encodeURIComponent("" + deviceModelId));
        url_ = url_.replace(/[?&]$/, "");

        let options_ : any = {
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Accept": "application/json"
            })
        };

        return this.http.request("get", url_, options_).pipe(_observableMergeMap((response_ : any) => {
            return this.processSelectActiveDeviceModelPortSpecification(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processSelectActiveDeviceModelPortSpecification(<any>response_);
                } catch (e) {
                    return <Observable<DeviceModelSpecificationSelectActiveOutput>><any>_observableThrow(e);
                }
            } else
                return <Observable<DeviceModelSpecificationSelectActiveOutput>><any>_observableThrow(response_);
        }));
    }

    protected processSelectActiveDeviceModelPortSpecification(response: HttpResponseBase): Observable<DeviceModelSpecificationSelectActiveOutput> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
            (<any>response).error instanceof Blob ? (<any>response).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); }}
        if (status === 200) {
            return this._commonFunctions.blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            let result200: any = null;
            let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result200 = DeviceModelSpecificationSelectActiveOutput.fromJS(resultData200);
            return _observableOf(result200);
            }));
        } else if (status !== 200 && status !== 204) {
            return this._commonFunctions.blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            return this._commonFunctions.throwException("An unexpected server error occurred.", status, _responseText, _headers);
            }));
        }
        return _observableOf<DeviceModelSpecificationSelectActiveOutput>(<any>null);
    }

    /**
     * @return Device model specification selected successfully.
     */
    selectADeviceModelPortSpecificationById(deviceModelPortSpecificationId: number): Observable<DeviceModelPortSpecificationSelectByIdOutput> {
        let url_ = this.baseUrl + "/DeviceModel/SelectADeviceModelPortSpecificationById/{deviceModelPortSpecificationId}";
        if (deviceModelPortSpecificationId === undefined || deviceModelPortSpecificationId === null)
            throw new Error("The parameter 'deviceModelPortSpecificationId' must be defined.");
        url_ = url_.replace("{deviceModelPortSpecificationId}", encodeURIComponent("" + deviceModelPortSpecificationId));
        url_ = url_.replace(/[?&]$/, "");

        let options_ : any = {
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Accept": "application/json"
            })
        };

        return this.http.request("get", url_, options_).pipe(_observableMergeMap((response_ : any) => {
            return this.processSelectADeviceModelPortSpecificationById(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processSelectADeviceModelPortSpecificationById(<any>response_);
                } catch (e) {
                    return <Observable<DeviceModelPortSpecificationSelectByIdOutput>><any>_observableThrow(e);
                }
            } else
                return <Observable<DeviceModelPortSpecificationSelectByIdOutput>><any>_observableThrow(response_);
        }));
    }

    protected processSelectADeviceModelPortSpecificationById(response: HttpResponseBase): Observable<DeviceModelPortSpecificationSelectByIdOutput> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
            (<any>response).error instanceof Blob ? (<any>response).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); }}
        if (status === 200) {
            return this._commonFunctions.blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            let result200: any = null;
            let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result200 = DeviceModelPortSpecificationSelectByIdOutput.fromJS(resultData200);
            return _observableOf(result200);
            }));
        } else if (status !== 200 && status !== 204) {
            return this._commonFunctions.blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            return this._commonFunctions.throwException("An unexpected server error occurred.", status, _responseText, _headers);
            }));
        }
        return _observableOf<DeviceModelPortSpecificationSelectByIdOutput>(<any>null);
    }
}
